import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaPhone, FaEnvelope, FaChevronDown, FaFire, FaTimes, FaHome, FaSearch, FaThList, FaPhoneAlt, FaChevronRight, FaMapMarkerAlt } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import './navbar.css';
import logo from '../../logo.png';
import { useApp } from '../../context/AppContext';
import Settings from '../settings/Settings';
import { getAnnouncementUrl } from '../../utils/slugify';
import { getApiEndpoint, getUploadUrl } from '../../config/api';

const Navbar = () => {
    const [showHotspotDropdown, setShowHotspotDropdown] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [featuredAnnouncements, setFeaturedAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [navbarFixed, setNavbarFixed] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const [quickFilters, setQuickFilters] = useState([]);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
    const [mobileSubcategoryOpen, setMobileSubcategoryOpen] = useState(null);
    const hotspotRef = useRef(null);
    const headerRef = useRef(null);
    const { t } = useApp();
    const navigate = useNavigate();
    const location = useLocation();

    // Toggle body scroll when mobile menu opens/closes
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.classList.add('machinery-mobile-menu-active');
        } else {
            document.body.classList.remove('machinery-mobile-menu-active');
        }
        
        return () => {
            document.body.classList.remove('machinery-mobile-menu-active');
        };
    }, [mobileMenuOpen]);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
        setMobileDropdownOpen(null);
        setMobileSubcategoryOpen(null);
    }, [location.pathname]);

    // Handle scroll for fixed navbar
    useEffect(() => {
        let headerInitialTop = 0;
        let ticking = false;
        
        // Store the initial top position after component mounts
        const timer = setTimeout(() => {
            if (headerRef.current) {
                headerInitialTop = headerRef.current.offsetTop;
            }
        }, 100);

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const shouldBeFixed = window.scrollY > headerInitialTop;
                    setNavbarFixed(shouldBeFixed);
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Fetch featured announcements on mount
    useEffect(() => {
        fetchFeaturedAnnouncements();
    }, []);

    // Detect active category from URL and generate quick filters
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const categoryParams = urlParams.getAll('category');

        if (location.pathname === '/machines' && categoryParams.length > 0) {
            // Use all selected categories
            setActiveCategory(categoryParams.join(','));
            
            // Find the parent category and siblings for the first/main category
            let foundQuickFilters = [];
            const mainCategoryId = categoryParams[0]; // Use first category to determine quick filters
            
            // Iterate through the categories data structure
            categories.forEach(mainCat => {
                mainCat.subcategories.forEach(subcat => {
                    // Check if this subcategory is active
                    if (subcat.id === mainCategoryId) {
                        // If it has items, show them as quick filters
                        if (subcat.items && subcat.items.length > 0) {
                            foundQuickFilters = subcat.items.map(item => ({
                                id: item.toLowerCase().replace(/\s+/g, '-'),
                                name: item,
                                parentId: subcat.id
                            }));
                        }
                    }
                    
                    // Check if an item within this subcategory is active
                    if (subcat.items) {
                        subcat.items.forEach(item => {
                            const itemId = item.toLowerCase().replace(/\s+/g, '-');
                            if (itemId === mainCategoryId) {
                                // Show sibling items as quick filters (including the active one for toggling)
                                foundQuickFilters = subcat.items.map(i => ({
                                    id: i.toLowerCase().replace(/\s+/g, '-'),
                                    name: i,
                                    parentId: subcat.id
                                }));
                            }
                        });
                    }
                });
            });
            
            setQuickFilters(foundQuickFilters);
        } else {
            setActiveCategory(null);
            setQuickFilters([]);
        }
    }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (hotspotRef.current && !hotspotRef.current.contains(event.target)) {
                closeHotspotDropdown();
            }
        };

        if (showHotspotDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showHotspotDropdown]);

    const fetchFeaturedAnnouncements = async () => {
        try {
            console.log('Fetching featured announcements...');
            const response = await fetch(getApiEndpoint('/api/announcements/featured'));
            const data = await response.json();
            
            console.log('Featured API Response:', data);
            
            if (data.success && data.announcements && data.announcements.length > 0) {
                const randomIndex = Math.floor(Math.random() * data.announcements.length);
                const randomAnnouncement = data.announcements[randomIndex];
                setFeaturedAnnouncements([randomAnnouncement]);
                console.log('Featured announcement selected:', randomAnnouncement.title);
            } else {
                console.log('No featured announcements found');
                setFeaturedAnnouncements([]);
            }
        } catch (error) {
            console.error('Error loading featured announcements:', error);
            setFeaturedAnnouncements([]);
        } finally {
            setIsLoading(false);
        }
    };

    const closeHotspotDropdown = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowHotspotDropdown(false);
            setIsClosing(false);
        }, 250);
    };

    const handleToggleHotspot = () => {
        if (showHotspotDropdown) {
            closeHotspotDropdown();
        } else {
            setShowHotspotDropdown(true);
        }
    };

    const handleAnnouncementClick = (announcement) => {
        navigate(getAnnouncementUrl(announcement.id, announcement.title, announcement.category, announcement.subcategory, announcement.created_at));
        closeHotspotDropdown();
    };

    const handleQuickFilterClick = (filterId, e) => {
        e.preventDefault();
        
        const urlParams = new URLSearchParams(location.search);
        
        // Get all current category values
        const currentCategories = urlParams.getAll('category');
        
        // Toggle the filter
        if (currentCategories.includes(filterId)) {
            // Remove if already selected
            urlParams.delete('category');
            currentCategories
                .filter(c => c !== filterId)
                .forEach(c => urlParams.append('category', c));
        } else {
            // Add to selection
            urlParams.append('category', filterId);
        }
        
        // Navigate with updated URL
        const newSearch = urlParams.toString();
        if (newSearch) {
            navigate(`/machines?${newSearch}`);
        } else {
            navigate('/machines');
        }
    };

    const getImageUrl = (announcement) => {
        if (announcement.images) {
            const imageArray = announcement.images.split(',').map(img => img.trim());
            if (imageArray.length > 0 && imageArray[0]) {
                return getUploadUrl(imageArray[0]);
            }
        }
        if (announcement.main_image) {
            return getUploadUrl(announcement.main_image);
        }
        return null;
    };

    const categories = [
        { 
            id: 'utilaje-constructii', 
            name: t.categoryNames['utilaje-constructii'],
            subcategories: [
                { id: 'excavatoare', name: t.categoryNames['excavatoare'],
                  items: ['excavatoare pe șenile', 'excavatoare pe roți', 'excavatoare pentru fier vechi', 'mini excavatoare']
                },
                { id: 'buldoexcavatoare', name: t.categoryNames['buldoexcavatoare'] },
                { id: 'buldozere', name: t.categoryNames['buldozere'] },
                { id: 'cilindri-compactori', name: t.categoryNames['cilindri-compactori'],
                  items: ['Cilindri compactori', 'Cilindri mici', 'Cilindri tandem', 'Cilindri Polipneu']
                },
                { id: 'gredere', name: t.categoryNames['gredere'] },
                { id: 'incarcatoare', name: t.categoryNames['incarcatoare'],
                  items: ['Încărcătoare frontale', 'Încărcătoare telescopice', 'Mini încărcătoare']
                },
                { id: 'dumpere', name: t.categoryNames['dumpere'] },
                { id: 'automacarale', name: t.categoryNames['automacarale'] },
                { id: 'autobetoniere', name: t.categoryNames['autobetoniere'] },
                { id: 'masini-asfalt', name: t.categoryNames['masini-asfalt'] },
                { id: 'sivuitoare', name: t.categoryNames['sivuitoare'] },
                { id: 'nacele', name: t.categoryNames['nacele'] },
                { id: 'statii-sortare', name: t.categoryNames['statii-sortare'] },
                { id: 'concasoare', name: t.categoryNames['concasoare'] },
                { id: 'accesorii-echipamente', name: t.categoryNames['accesorii-echipamente'] },
                { id: 'dezmembrari', name: t.categoryNames['dezmembrari'] },
                { id: 'diverse', name: t.categoryNames['diverse'] }
            ]
        },
        { 
            id: 'transport', 
            name: t.categoryNames['transport'],
            subcategories: [
                { id: 'camioane', name: t.categoryNames['camioane'] },
                { id: 'trailere', name: t.categoryNames['trailere'] },
                { id: 'semiremorci', name: t.categoryNames['semiremorci'] },
                { id: 'matura-stradala', name: t.categoryNames['matura-stradala'] }
            ]
        },
        { 
            id: 'utilaje-agricole', 
            name: t.categoryNames['utilaje-agricole'],
            subcategories: [
                { id: 'tractoare', name: t.categoryNames['tractoare'] },
                { id: 'combine', name: t.categoryNames['combine'] },
                { id: 'echipamente-agricole', name: t.categoryNames['echipamente-agricole'] }
            ]
        },
        {
            id: 'autoturisme-autoutilitare',
            name: t.categoryNames['autoturisme-autoutilitare'],
            subcategories: [
                { id: 'autoturisme', name: t.categoryNames['autoturisme'] },
                { id: 'autoutilitare', name: t.categoryNames['autoutilitare'] },
                { id: 'remorci', name: t.categoryNames['remorci'] }
            ]
        }
    ];

    return (
        <>
            {/* Placeholder when header is fixed */}
            {navbarFixed && <div style={{ height: headerRef.current?.offsetHeight + 'px' }}></div>}
            
            <header ref={headerRef} className={`machinery-header ${navbarFixed ? 'machinery-header-fixed' : ''}`}>
                {/* Top Bar */}
                <div className="machinery-top-bar">
                    <div className="machinery-container">
                        <div className="machinery-top-bar-content">
                            <div className="machinery-contact-info">
                                <a href={`tel:${t.header.contact.phone}`} className="machinery-contact-item">
                                    <FaPhone /> {t.header.contact.phone}
                                </a>
                                <a href={`mailto:${t.header.contact.email}`} className="machinery-contact-item">
                                    <FaEnvelope /> {t.header.contact.email}
                                </a>
                            </div>
                            <div className="machinery-top-bar-right">
                                <div className="machinery-top-bar-text">
                                    <span>🚛 {t.header.topBarText}</span>
                                </div>
                                <Settings />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Navigation */}
                <nav className="machinery-navbar">
                <div className="machinery-container machinery-nav-container">
                    <div className="machinery-logo">
                        <a href="/home"><img src={logo} alt="MachineryA3 Logo" className="machinery-logo-image" /></a>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button 
                        className="machinery-menu-toggle"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
                    </button>

                    {/* Desktop Menu */}
                    <ul className="machinery-nav-menu-navbar">
                        <li>
                            <a href="/home">
                                <span>{t.header.nav.home}</span>
                            </a>
                        </li>
                        <li>
                            <a href="/machines">
                                <span>{t.header.nav.searchMachinery}</span>
                            </a>
                        </li>
                        <li className="machinery-nav-dropdown">
                            <a 
                                href="/categories" 
                                className="machinery-dropdown-toggle"
                            >
                                <span>{t.header.nav.allCategories}</span>
                                <FaChevronDown className="machinery-dropdown-icon" />
                            </a>
                            <div className="machinery-dropdown-menu machinery-categories-dropdown">
                                <div className="machinery-dropdown-header">
                                    <span className="machinery-dropdown-title">{t.header.nav.allCategories}</span>
                                    <span className="machinery-dropdown-subtitle">{t.header.nav.selectCategory}</span>
                                </div>
                                {categories.map(category => (
                                    <div 
                                        key={category.id} 
                                        className="machinery-category-wrapper"
                                    >
                                        <a 
                                            href={`/machines?category=${category.id}`} 
                                            className="machinery-dropdown-item"
                                        >
                                            <span className="machinery-category-name">{category.name}</span>
                                            {category.subcategories && (
                                                <span className="machinery-has-submenu-icon">›</span>
                                            )}
                                        </a>
                                        {category.subcategories && (
                                            <div className="machinery-submenu">
                                                {category.subcategories.map((sub, idx) => (
                                                    <div 
                                                        key={sub.id} 
                                                        className="machinery-submenu-group"
                                                    >
                                                        {sub.items && sub.items.length > 0 ? (
                                                            <a
                                                                href={`/machines?category=${sub.id}`}
                                                                className="machinery-submenu-item"
                                                            >
                                                                <span className="machinery-submenu-number">{idx + 1}.</span>
                                                                {sub.name}
                                                                <span className="machinery-submenu-arrow">›</span>
                                                            </a>
                                                        ) : (
                                                            <a 
                                                                href={`/machines?category=${sub.id}`}
                                                                className="machinery-submenu-item"
                                                            >
                                                                <span className="machinery-submenu-number">{idx + 1}.</span>
                                                                {sub.name}
                                                            </a>
                                                        )}
                                                        {sub.items && sub.items.length > 0 && (
                                                            <div className="machinery-submenu-nested">
                                                                <a
                                                                    href={`/machines?category=${sub.id}`}
                                                                    className="machinery-submenu-nested-item all-items"
                                                                >
                                                                    ● Toate {sub.name}
                                                                </a>
                                                                {sub.items.map(item => {
                                                                    const itemId = item.toLowerCase().replace(/\s+/g, '-');
                                                                    return (
                                                                        <a
                                                                            key={itemId}
                                                                            href={`/machines?category=${itemId}`}
                                                                            className="machinery-submenu-nested-item"
                                                                        >
                                                                            ○ {t.subsubCategories[item] || item}
                                                                        </a>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </li>
                        <li>
                            <a href="/contact">
                                <span>{t.header.nav.contact}</span>
                            </a>
                        </li>
                        
                        {/* HOTSPOT - Bottom CTA */}
                        <li className="machinery-nav-dropdown">
                            <button 
                                className={`hotspotarea-trigger ${showHotspotDropdown ? 'machinery-active' : ''}`}
                                onClick={handleToggleHotspot}
                            >
                                <FaFire className="hotspotarea-fire-icon" />
                                <span className="hotspotarea-text">{t.header.nav.hotspot}</span>
                            </button>
                        </li>
                    </ul>

                    {/* Hotspot Dropdown - Rendered outside nav for mobile compatibility */}
                    {showHotspotDropdown && (
                        <>
                            <div 
                                className="machinery-hotspot-backdrop"
                                onClick={closeHotspotDropdown}
                            />
                            <div className={`hotspotarea-dropdown ${isClosing ? 'machinery-closing' : ''}`}>
                                    <div className="hotspotarea-header">
                                        <div className="hotspotarea-header-content">
                                            <FaFire className="hotspotarea-header-fire-icon" />
                                            <div>
                                                <h3 className="hotspotarea-title">{t.header.nav.hotspotTitle}</h3>
                                                <p className="hotspotarea-subtitle">{t.header.nav.hotspotSubtitle}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hotspotarea-content">
                                        {isLoading ? (
                                            <div className="hotspotarea-loading">
                                                <div className="hotspotarea-spinner"></div>
                                                <p>{t.header.nav.loadingOffers}</p>
                                            </div>
                                        ) : featuredAnnouncements.length > 0 ? (
                                            <div className="hotspotarea-list">
                                                {featuredAnnouncements.map((announcement, index) => {
                                                    const imageUrl = getImageUrl(announcement);
                                                    return (
                                                        <div
                                                            key={announcement.id}
                                                            className="hotspotarea-item"
                                                            onClick={() => handleAnnouncementClick(announcement)}
                                                            style={{ animationDelay: `${index * 0.05}s` }}
                                                        >
                                                            <div className="hotspotarea-image-wrapper">
                                                                {imageUrl ? (
                                                                    <img 
                                                                        src={imageUrl} 
                                                                        alt={announcement.title_en || announcement.title}
                                                                        className="hotspotarea-image"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                <div className="hotspotarea-no-image" style={{ 
                                                                    display: imageUrl ? 'none' : 'flex'
                                                                }}>
                                                                    🚜
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="hotspotarea-details">
                                                                <div className="hotspotarea-badges-row">
                                                                    <div className="hotspotarea-category-badge">
                                                                        {announcement.category || 'Machinery'}
                                                                    </div>
                                                                    
                                                                    {announcement.condition && (
                                                                        <div className="hotspotarea-condition-badge">
                                                                            {announcement.condition}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {announcement.negotiable === 1 && (
                                                                        <div className="hotspotarea-negotiable-badge">
                                                                            💰 {t.header.nav.negotiable}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                <h4 className="hotspotarea-item-title">
                                                                    {announcement.title_en || announcement.title}
                                                                </h4>
                                                                
                                                                <div className="hotspotarea-stats">
                                                                    {announcement.year && (
                                                                        <div className="hotspotarea-stat-item">
                                                                            <span className="hotspotarea-features">{t.header.nav.year}</span>
                                                                            <span className="hotspotarea-year">{announcement.year}</span>
                                                                        </div>
                                                                    )}
                                                                    {announcement.hours && (
                                                                        <div className="hotspotarea-stat-item">
                                                                            <span className="hotspotarea-features">{t.header.nav.hours}</span>
                                                                            <span className="hotspotarea-hours">{announcement.hours}H</span>
                                                                        </div>
                                                                    )}
                                                                    {announcement.location && (
                                                                        <div className="hotspotarea-stat-item">
                                                                            <span className="hotspotarea-features">{t.header.nav.location}</span>
                                                                            <span className="hotspotarea-location">{announcement.location}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="hotspotarea-price-section">
                                                                    <span className="hotspotarea-price-label">{t.header.nav.price}</span>
                                                                    <div className="hotspotarea-price-value">
                                                                        <span className="hotspotarea-currency">€</span>
                                                                        <span className="hotspotarea-price-amount">
                                                                            {announcement.price ? parseInt(announcement.price).toLocaleString() : 'N/A'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="hotspotarea-empty">
                                                <div className="hotspotarea-empty-icon">🔍</div>
                                                <p>{t.header.nav.noOffersAvailable}</p>
                                            </div>
                                        )}
                                    </div>

                                    {featuredAnnouncements.length > 0 && (
                                        <div className="hotspotarea-footer">
                                            <button 
                                                className="hotspotarea-view-all"
                                                onClick={() => {
                                                    navigate(`/announcement/${featuredAnnouncements[0].id}`);
                                                    closeHotspotDropdown();
                                                }}
                                            >
                                                {t.header.nav.viewFullDetails}
                                            </button>
                                        </div>
                                    )}
                            </div>
                        </>
                    )}

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <div 
                            className="machinery-mobile-backdrop"
                            onClick={() => setMobileMenuOpen(false)}
                        />
                    )}
                    <div className={`machinery-mobile-menu ${mobileMenuOpen ? 'machinery-mobile-menu-open' : ''}`}>
                        <div className="machinery-mobile-menu-content">
                            <div className="machinery-mobile-menu-header">
                                <div className="machinery-mobile-logo">
                                    <img src={logo} alt="MachineryA3" />
                                </div>
                                <button 
                                    className="machinery-mobile-close"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <nav className="machinery-mobile-nav">
                                {/* Hotspot Button - Featured at top */}
                                <button
                                    ref={hotspotRef}
                                    className="machinery-mobile-hotspot-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleHotspot();
                                    }}
                                >
                                    <FaFire className="machinery-mobile-hotspot-icon" />
                                    <span className="machinery-mobile-hotspot-text">{t.header.nav.hotspot}</span>
                                    <span className="machinery-mobile-hotspot-badge">🔥</span>
                                </button>

                                <a 
                                    href="/home" 
                                    className="machinery-mobile-nav-item"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <FaHome className="machinery-mobile-nav-icon" />
                                    <span>{t.header.nav.home}</span>
                                </a>

                                <a 
                                    href="/machines" 
                                    className="machinery-mobile-nav-item"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <FaSearch className="machinery-mobile-nav-icon" />
                                    <span>{t.header.nav.searchMachinery}</span>
                                </a>

                                <div className="machinery-mobile-dropdown">
                                    <button 
                                        className="machinery-mobile-dropdown-toggle"
                                        onClick={() => setMobileDropdownOpen(mobileDropdownOpen === 'categories' ? null : 'categories')}
                                    >
                                        <div className="machinery-mobile-dropdown-left">
                                            <FaThList className="machinery-mobile-nav-icon" />
                                            <span>{t.header.nav.allCategories}</span>
                                        </div>
                                        <FaChevronDown className={`machinery-mobile-dropdown-arrow ${mobileDropdownOpen === 'categories' ? 'machinery-mobile-dropdown-arrow-open' : ''}`} />
                                    </button>

                                    {mobileDropdownOpen === 'categories' && (
                                        <div className="machinery-mobile-submenu">
                                            {categories.map((category, catIndex) => (
                                                <div key={category.id} className="machinery-mobile-category">
                                                    {category.subcategories ? (
                                                        <>
                                                            <button 
                                                                className="machinery-mobile-subcategory-toggle"
                                                                onClick={() => setMobileSubcategoryOpen(mobileSubcategoryOpen === category.id ? null : category.id)}
                                                            >
                                                                <span>{category.name}</span>
                                                                <FaChevronRight className={`machinery-mobile-subcategory-arrow ${mobileSubcategoryOpen === category.id ? 'machinery-mobile-subcategory-arrow-open' : ''}`} />
                                                            </button>
                                                            {mobileSubcategoryOpen === category.id && (
                                                                <div className="machinery-mobile-subcategory-list">
                                                                    {category.subcategories.map((sub, subIdx) => (
                                                                        <a 
                                                                            key={sub.id}
                                                                            href={`/machines?category=${sub.id}`}
                                                                            className="machinery-mobile-subcategory-item"
                                                                            onClick={() => {
                                                                                setMobileMenuOpen(false);
                                                                                setMobileDropdownOpen(null);
                                                                                setMobileSubcategoryOpen(null);
                                                                            }}
                                                                        >
                                                                            <span className="machinery-mobile-subcategory-number">{subIdx + 1}.</span>
                                                                            <span>{sub.name}</span>
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <a 
                                                            href={`/machines?category=${category.id}`}
                                                            className="machinery-mobile-category-link"
                                                            onClick={() => {
                                                                setMobileMenuOpen(false);
                                                                setMobileDropdownOpen(null);
                                                            }}
                                                        >
                                                            {category.name}
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <a 
                                    href="/contact" 
                                    className="machinery-mobile-nav-item"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <FaMapMarkerAlt className="machinery-mobile-nav-icon" />
                                    <span>{t.header.nav.contact}</span>
                                </a>

                                <a 
                                    href={`tel:${t.header.contact.phone}`}
                                    className="machinery-mobile-nav-item machinery-mobile-contact"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <FaPhoneAlt className="machinery-mobile-nav-icon" />
                                    <span>{t.header.contact.phone}</span>
                                </a>

                                <a 
                                    href={`mailto:${t.header.contact.email}`}
                                    className="machinery-mobile-nav-item machinery-mobile-contact"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <FaEnvelope className="machinery-mobile-nav-icon" />
                                    <span>{t.header.contact.email}</span>
                                </a>
                            </nav>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Quick Filters Bar - Shows when a category is active */}
            {activeCategory && quickFilters.length > 0 && (
                <div className="machinery-quick-filters-bar">
                    <div className="machinery-container">
                        <div className="machinery-quick-filters-content">
                            <span className="machinery-quick-filters-label">
                                {t.header.nav.quickFilters}
                            </span>
                            <div className="machinery-quick-filters-list">
                                {quickFilters.slice(0, 5).map(filter => {
                                    const urlParams = new URLSearchParams(location.search);
                                    const currentCategories = urlParams.getAll('category');
                                    const isActive = currentCategories.includes(filter.id);
                                    
                                    return (
                                        <button
                                            key={filter.id}
                                            onClick={(e) => handleQuickFilterClick(filter.id, e)}
                                            className={`machinery-quick-filter-btn ${isActive ? 'active' : ''}`}
                                        >
                                            {filter.name}
                                        </button>
                                    );
                                })}
                                {quickFilters.length > 5 && (
                                    <span className="machinery-quick-filters-more">
                                        +{quickFilters.length - 5} mai multe
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
        </>
    );
};

export default Navbar;
